'use client'

import React from 'react'
import { Box, Button, IconButton, Modal, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Close, LocalOffer, OpenInNew } from '@mui/icons-material'

import { MediaPlayer } from './mediaPlayer'
import { progressAnimation } from './mediaFloater'
import type { MediaFloaterModalProps } from './types'

const MODAL_MAX_WIDTH = 420

const MediaFloaterModal: React.FC<MediaFloaterModalProps> = ({
    mediaItems,
    currentIndex,
    currentDurationMs,
    onClose,
    onMediaAdvance,
    onMediaPrevious,
    onVideoDurationChange,
    exploreOffersUrl
}) => {
    const currentItem = mediaItems[currentIndex]
    const hasCta = Boolean(currentItem?.ctaText)
    const hasBottomOverlay = Boolean(currentItem?.caption || currentItem?.location || hasCta || exploreOffersUrl)
    const bottomReservedPx = hasCta ? 160 : hasBottomOverlay ? 100 : 0

    return (
        <Modal
            open={true}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                // Desktop: soft gradient bg visible around the card
                // Mobile: plain black
                background: { xs: 'black', md: 'linear-gradient(135deg, #f0f0f0 0%, #e4e4e4 50%, #ececec 100%)' }
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        background: { xs: 'black', md: 'linear-gradient(135deg, #f0f0f0 0%, #e4e4e4 50%, #ececec 100%)' }
                    }
                }
            }}
        >
            {/* Card — full screen on mobile, centered 80dvh card on desktop */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    maxWidth: { xs: '100%', md: MODAL_MAX_WIDTH },
                    height: { xs: '100dvh', md: '80dvh' },
                    bgcolor: 'common.black',
                    borderRadius: { xs: 0, md: '20px' },
                    overflow: 'hidden',
                    outline: 'none',
                    boxShadow: { xs: 'none', md: '0 12px 48px rgba(0,0,0,0.30), 0 2px 10px rgba(0,0,0,0.15)' },
                    mx: { xs: 0, md: 2 }
                }}
            >
                {/* ── Progress bars ── */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        display: 'flex',
                        gap: 0.5,
                        zIndex: 4,
                        px: 1.5,
                        pt: 'max(env(safe-area-inset-top), 14px)',
                        pb: 1
                    }}
                >
                    {mediaItems.map((item, index) => {
                        const isActive = index === currentIndex
                        const barDurationMs = isActive ? currentDurationMs : item.duration ?? 3000
                        return (
                            <Box
                                key={index}
                                sx={{
                                    flex: 1,
                                    height: 2,
                                    bgcolor: theme => alpha(theme.palette.common.white, 0.35),
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}
                            >
                                <Box
                                    key={isActive ? `active-${currentIndex}-${currentDurationMs}` : `inactive-${index}`}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        height: '100%',
                                        bgcolor: 'common.white',
                                        borderRadius: 1,
                                        ...(index < currentIndex
                                            ? { width: '100%' }
                                            : isActive
                                              ? { width: '0%', animation: `${progressAnimation} ${barDurationMs}ms linear forwards` }
                                              : { width: '0%' })
                                    }}
                                />
                            </Box>
                        )
                    })}
                </Box>

                {/* ── Media ── */}
                <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
                    <MediaPlayer
                        mediaItem={currentItem}
                        onNext={onMediaAdvance}
                        autoPlay={true}
                        isInModal={true}
                        priority={currentIndex === 0}
                        onDurationChange={onVideoDurationChange}
                        tapToMute={true}
                    />
                </Box>

                {/* ── Tap zones ── */}
                <Box
                    onClick={onMediaPrevious}
                    sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: '50%',
                        height: `calc(100% - ${bottomReservedPx}px)`,
                        cursor: currentIndex > 0 ? 'pointer' : 'default',
                        zIndex: 1
                    }}
                />
                <Box
                    onClick={onMediaAdvance}
                    sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        width: '50%',
                        height: `calc(100% - ${bottomReservedPx}px)`,
                        cursor: 'pointer',
                        zIndex: 1
                    }}
                />

                {/* ── Close button ── */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 'max(env(safe-area-inset-top), 12px)',
                        right: 12,
                        mt: 3.5,
                        bgcolor: theme => alpha(theme.palette.common.black, 0.5),
                        color: 'common.white',
                        backdropFilter: 'blur(4px)',
                        '&:hover': { bgcolor: theme => alpha(theme.palette.common.black, 0.7) },
                        zIndex: 5,
                        width: 40,
                        height: 40
                    }}
                    size="small"
                >
                    <Close sx={{ fontSize: 20 }} />
                </IconButton>

                {/* ── Bottom overlay — caption + location + CTA ── */}
                {hasBottomOverlay && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            zIndex: 3,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.4) 55%, transparent 100%)',
                            px: 2.5,
                            pt: 6,
                            pb: 'max(env(safe-area-inset-bottom), 24px)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.75
                        }}
                    >
                        {currentItem?.badge && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    alignSelf: 'flex-start',
                                    gap: 0.5,
                                    bgcolor: '#FF6B00',
                                    color: 'common.white',
                                    borderRadius: '8px',
                                    px: 1,
                                    py: 0.4
                                }}
                            >
                                <LocalOffer sx={{ fontSize: 13 }} />
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: 0.2 }}>
                                    {currentItem.badge}
                                </Typography>
                            </Box>
                        )}

                        {currentItem?.caption && (
                            <Typography
                                sx={{
                                    color: 'common.white',
                                    fontWeight: 700,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                                    lineHeight: 1.3,
                                    pr: hasCta ? 0 : 5
                                }}
                            >
                                {currentItem.caption}
                            </Typography>
                        )}

                        {currentItem?.location && (
                            <Typography
                                sx={{
                                    color: theme => alpha(theme.palette.common.white, 0.82),
                                    fontSize: '0.85rem',
                                    lineHeight: 1.2
                                }}
                            >
                                {currentItem.location}
                            </Typography>
                        )}

                        {hasCta && (
                            <Button
                                component={currentItem?.ctaUrl ? 'a' : 'button'}
                                href={currentItem?.ctaUrl}
                                target={currentItem?.ctaUrl ? '_blank' : undefined}
                                rel={currentItem?.ctaUrl ? 'noopener noreferrer' : undefined}
                                variant="contained"
                                fullWidth
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                sx={{
                                    mt: 1,
                                    borderRadius: 2,
                                    py: 1.25,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    textTransform: 'none',
                                    bgcolor: 'common.white',
                                    color: 'text.primary',
                                    '&:hover': { bgcolor: 'grey.100' }
                                }}
                            >
                                {currentItem?.ctaText}
                            </Button>
                        )}

                        {exploreOffersUrl && (
                            <Box
                                component="a"
                                href={exploreOffersUrl}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    mt: 1,
                                    color: theme => alpha(theme.palette.common.white, 0.75),
                                    fontSize: '0.8rem',
                                    fontWeight: 500,
                                    textDecoration: 'none',
                                    '&:hover': { color: 'common.white' }
                                }}
                            >
                                Explore all offers
                                <OpenInNew sx={{ fontSize: 12 }} />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Modal>
    )
}

export { MediaFloaterModal }
export type { MediaFloaterModalProps }
